import { getValueList } from '../../../../sources/value';
import type { FormalValueReference, SubflowArgument } from '../../../../types/graph';
import type { ValueElementIdReferenceAPI } from '../../../../types/value';

export const generateSubFlowArgumentSelectors = async (
    access: string,
    subFlowId: string,
    valueReferences: ValueElementIdReferenceAPI[],
    subFlowArguments: SubflowArgument[],
): Promise<FormalValueReference[]> => {
    const params = { access, flow: subFlowId };

    // This tells us what values the subflow will accept as arguments
    const formalValueRefs = await getValueList(params);

    return formalValueRefs
        .filter((value) => !value.typeElementPropertyId)
        .map((value) => {
            // Grab the corresponding argument from the subflow metadata
            const arg = subFlowArguments
                ? subFlowArguments.find((a) => a.valueElementInSubflowId.id === value.id)
                : null;

            // Now find the value that will be passed by mapping the argument ID to the value ID
            const actualArgument = arg
                ? valueReferences.find(
                      (avf) =>
                          avf.id === arg.valueElementToApplyId.id &&
                          avf.typeElementPropertyId ===
                              arg.valueElementToApplyId.typeElementPropertyId,
                  ) || null
                : null;

            // Return an object that we can give to a value selector
            return {
                // Should we display the value picker modal?
                isOpen: false,
                id: crypto.randomUUID(),

                // The value access type (used for filter what values to display)
                access,

                // The name of the value argument from inside the subflow
                formalValueName: value.developerName,

                // The ID of the value argument from inside the subflow
                formalValueId: value.id,

                // The type element property ID of the value argument from inside the subflow
                formalValueTypeElementPropertyId: value.typeElementPropertyId,

                // The formatted value metatdata used for displaying a selected value
                // in the value selector component. In this case this would be the
                // value selected for passing to/from the parent flow to the child flow
                selectedValueMeta: actualArgument,
            };
        });
};

export const matchAllSubFlowArguments = (
    valueReferences: ValueElementIdReferenceAPI[],
    onMatch: (matches: FormalValueReference[], matchCount: number) => void,
    subFlowArgumentsToBeRendered: FormalValueReference[],
) => {
    const selectedValuesToBeMatched = subFlowArgumentsToBeRendered.map(
        (subFlowArgumentToBeRendered) => {
            const matchingValue = valueReferences.find(
                (value) =>
                    value.id === subFlowArgumentToBeRendered.formalValueId &&
                    value.typeElementPropertyId ===
                        subFlowArgumentToBeRendered.formalValueTypeElementPropertyId,
            ) as ValueElementIdReferenceAPI;

            return {
                ...subFlowArgumentToBeRendered,
                selectedValueMeta: matchingValue,
            };
        },
    );

    onMatch(selectedValuesToBeMatched, selectedValuesToBeMatched.length);
};
